import { useCallback, useEffect, useRef, useState } from 'react';
import apiClient from '../../utils/api-client';
import getColumnClass from '../../utils/getColumnClass';
import CardSkeleton from '../Common/CardSkeleton';
import GridControls from '../Common/GridControls';
import VideoProductCard from '../Shop/VideoProductCard';
import PageHeader from './../Common/PageHeader';

const VideosPage = () => {
  const [products, setProducts] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const productContainerRef = useRef(null);
  const [gridSize, setGridSize] = useState(() => {
    const storeGridZize = localStorage.getItem('VideosGridSize');
    return storeGridZize ? parseInt(storeGridZize, 10) : 4;
  });
  useEffect(() => {
    localStorage.setItem('VideosGridSize', gridSize.toString());
  }, [gridSize]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchProducts = useCallback(async (page) => {
    console.log(`Fetching page: ${page}`); // Debug log
    try {
      setIsFetching(true);
      const res = await apiClient.post('/is-youtube', { page });
      const newProducts = res.data.data;
      const fetchedTotalPages = res.data.total_pages;
      setTotalPages(fetchedTotalPages);

      setProducts((prevProducts) => {
        const uniqueNewProducts = newProducts.filter(
          (newProduct) =>
            !prevProducts.some(
              (product) => product.product_id === newProduct.product_id
            )
        );
        return [...prevProducts, ...uniqueNewProducts];
      });
      setIsLoading(false);
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    } finally {
      setIsFetching(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts(page);
  }, [fetchProducts, page]);

  const handleScroll = useCallback(() => {
    if (
      !isFetching &&
      productContainerRef.current &&
      productContainerRef.current.getBoundingClientRect().bottom <=
        window.innerHeight + 10 &&
      page < totalPages
    ) {
      console.log(`Adding page: ${page + 1} to queue`); // Debug log
      setPage((prevPage) => prevPage + 1);
    }
  }, [isFetching, page, totalPages]);

  useEffect(() => {
    const debouncedHandleScroll = debounce(handleScroll, 200);

    window.addEventListener('scroll', debouncedHandleScroll);

    return () => {
      window.removeEventListener('scroll', debouncedHandleScroll);
    };
  }, [handleScroll]);

  // Debounce function to limit the rate of function execution
  function debounce(func, wait) {
    let timeout;
    return function (...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  return (
    <>
      <PageHeader title="Videos" />
      <div className="container-fluid">
        <GridControls
          gridSize={gridSize}
          setGridSize={setGridSize}
          windowWidth={windowWidth}
          allowedGridSizes={[3, 4]} // only allow 3 & 4
        />
      </div>
      <section className="videos_product_section" ref={productContainerRef}>
        <div className="container-fluid">
          <div className="row justify-content-center">
            {error && <em className="form_error">{error}</em>}
            {isLoading && <CardSkeleton cards={8} />}
            {products &&
              products.map((product) => (
                <div
                  className={`${getColumnClass(gridSize, windowWidth)} p-2`}
                  key={product.product_id}
                >
                  <VideoProductCard
                    key={product.product_id}
                    product={product}
                    youtubeVideoUrl={product.youtube_video_id}
                  />
                </div>
              ))}
            {isFetching && <CardSkeleton cards={8} />}
          </div>
        </div>
      </section>
    </>
  );
};

export default VideosPage;
